import { useForm } from 'react-hook-form';
import { axiosInstance } from '@/services/axios.service';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Button } from 'primereact/button';
import { FlexWrap, Row } from '@/components/container/Container';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BaseResponse, GenericResponse, ListResponse } from '@/common/common_utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { cn } from '@/components/ui/cn';
import { TextLabelLarge2, TextLabelMedium2 } from '@/components/text/Texts';
import { ENDPOINTS } from '@/constants/endpoints';
import { MdClose } from 'react-icons/md';
import TextInput from '@/components/inputs/TextInput';
import { InputController } from '@/components/inputs/InputController';

interface TrustedDomainListItem { Id: number; Domain: string; }
interface AddTrustedDomainRequest { domain: string }
interface DeleteTrustedDomainRequest { id: number }

const domainQueryKey = ['SettingsGeneralTab.domains'];
const restrictedDomains: string[] = [
	'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'aol.com', 'mail.ru', 'yandex.ru', 'yandex.com', 'rambler.ru',
	'protonmail.com', 'proton.me', 'zoho.com', 'gmx.com', 'gmx.net', 'web.de', 'inbox.lv', 'ukr.net', 'bk.ru', 'list.ru',
	'tuta.io', 'tutanota.com', 'hushmail.com', 'fastmail.com', 'live.com', 'msn.com', 'me.com', 'qq.com', '163.com', '126.com',
	'sina.com', 'yeah.net', 'lycos.com', 'aim.com', 'icqmail.com', 'rediffmail.com', 'india.com', 'zoho.in', 'laposte.net', 'libero.it',
	'virgilio.it', 'seznam.cz', 'centrum.cz', 'wp.pl', 'o2.pl', 'onet.pl', 'freemail.hu', 'bol.com.br', 'uol.com.br', 'terra.com.br',
];

const AddDomainSection = () => {
	// its really stupid that we cant use single controls without form
	const domainForm = useForm<{ domain: string }>({
		defaultValues: { domain: '' },
		mode: 'onChange',
		resolver: yupResolver(yup.object({
			domain: yup
				.string()
				.trim()
				.required(VALIDATION_MESSAGES.required)
				.matches(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, 'Enter a valid domain'),
		})),
	});

	const queryClient = useQueryClient();
	const { data: domains = [] } = useQuery({
		queryKey: domainQueryKey,
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<TrustedDomainListItem>>>(
				ENDPOINTS['/api/X4bCompanyTrustedDomain/GetTrustedDomains']
			);
			return result.data.Data.Items;
		}
	});

	const deleteDomainMutation = useMutation({
		mutationFn: async (id: number) => {
			const response = await axiosInstance.post<BaseResponse>(
				ENDPOINTS['/api/X4bCompanyTrustedDomain/DeleteTrustedDomain'],
				{ id } as DeleteTrustedDomainRequest
			);

			if (!response.data.Success) throw new Error(response.data.ErrorMessage || 'Failed to delete domain');
			return response.data;
		},
		onSuccess: () => {
			showNotification('Domain deleted successfully');
			queryClient.invalidateQueries({ queryKey: domainQueryKey });
		},
		onError: (error) => showNotification(error.message || 'Failed to delete domain'),
	});

	const addDomainMutation = useMutation({
		mutationFn: async (domain: string) => {
			const response = await axiosInstance.post<BaseResponse>(
				ENDPOINTS['/api/X4bCompanyTrustedDomain/AddTrustedDomain'],
				{ domain } as AddTrustedDomainRequest
			);

			if (!response.data.Success) throw new Error(response.data.ErrorMessage || 'Failed to add domain');
			return response.data;
		},
		onSuccess: () => {
			showNotification('Domain added successfully');
			queryClient.invalidateQueries({ queryKey: domainQueryKey });
		},
		onError: (error: any) => showNotification(error.message || 'Failed to add domain'),
	});

	const isBtnDisabled = addDomainMutation.isPending || !domainForm.formState.isValid;
	const hasDomains = domains.length > 0;
	const hasRestrictedDomains = domains.some((domain) => restrictedDomains.includes(domain.Domain));

	return (
		<>
			<Row className="gap-6">
				<InputController id="domain" control={domainForm.control}>
					<TextInput label="Trusted Domain" placeholder="Add a trusted domain" containerCls='w-[516px]' />
				</InputController>

				<Button
					id="add-trusted-domain"
					label="Add domain"
					onClick={() => addDomainMutation.mutate(domainForm.getValues('domain').trim())}
					disabled={isBtnDisabled}
					pt={{
						root: { className: cn('h-14 px-6 py-4 rounded-[100px] duration-300', isBtnDisabled ? 'bg-[#D1D4D6]' : 'bg-primary') },
						label: { className: cn('font-poppins text-label-large2 duration-300', isBtnDisabled ? 'text-[#8A8B8C]' : 'text-white') },
					}}
				/>
			</Row>

			{hasDomains && hasRestrictedDomains && (
				<TextLabelMedium2
					className="text-primary"
					text="Your organization has restricted domains. Please contact us to update your list of trusted domains."
				/>
			)}
			{hasDomains && (
				<FlexWrap className="gap-2">
					{domains.map((domain) => (
						<Row className={cn(
							'h-8 pl-4 pr-1 items-center border rounded-[8px] gap-2',
							restrictedDomains.includes(domain.Domain) ? 'bg-[#F9DEDC] border-error90' : 'bg-[#E8F7F0] border-success90'
						)}>
							<TextLabelLarge2
								className={restrictedDomains.includes(domain.Domain) ? 'text-error60' : 'text-success60'}
								text={domain.Domain}
							/>
							<button
								onClick={() => deleteDomainMutation.mutate(domain.Id)}
								className="rounded-full p-1 duration-200 hover:bg-[#00000011]"
								children={(<MdClose size={18} className="text-black" />)}
							/>
						</Row>
					))}
				</FlexWrap>
			)}
			{hasDomains && hasRestrictedDomains && (
				<TextLabelMedium2
					className="text-primary"
					text="Domains highlighted in red may not be secure. Consider reviewing and removing these domains."
				/>
			)}
		</>
	);
};

export default AddDomainSection;
